import { GroupByMode, newTaskSuffix } from '@griply/types';
import { getNewTaskPrefill, onSectionReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { focusOnEndOfEditableContent } from 'shared/utils/focus-on-end-of-element';

import { useSectionTree } from '../../hooks/use-section-tree';
import { GroupedListProps, noGroupingId } from '../../types';
import { SectionHeader } from '../section-header/section-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './section-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task, TaskListItemTypes.Section];

const isItemDraggable = (item: TreeItem) => item.id !== noGroupingId;

export const SectionGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  filterDocument,
  isContextMenuOpen,
}) => {
  const dropTypes: Record<TaskListItemTypes, TaskListItemTypes[]> = useMemo(
    () => ({
      [TaskListItemTypes.Root]: [
        TaskListItemTypes.Section,
        TaskListItemTypes.Task,
      ],
      [TaskListItemTypes.Section]: [TaskListItemTypes.Task],
      [TaskListItemTypes.Task]: [],
      [TaskListItemTypes.NewTask]: [],
      [TaskListItemTypes.Goal]: [],
      [TaskListItemTypes.LifeArea]: [],
      [TaskListItemTypes.Priority]: [],
      [TaskListItemTypes.Date]: [],
      [TaskListItemTypes.DayPart]: [],
      [TaskListItemTypes.Tag]: [],
      [TaskListItemTypes.Type]: [],
    }),
    [],
  );

  const {
    sections = [],
    showEmptyGroups = true, // Sections show empty by default
    onUpdateSection,
    goalId,
    lifeAreaId,
  } = groupSettings;
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useSectionTree({
    tasks,
    sections,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onSectionReorder({
      updatedItems,
      hasSections: !!sections?.length,
      rootSectionId: rootId,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const onUpdateSectionRef = useRef(onUpdateSection);
  onUpdateSectionRef.current = onUpdateSection;

  // Map of section IDs to their new task input elements
  const newTaskInputRefs = useRef<Record<ID, HTMLDivElement | null>>({});

  const handleAddTask = (sectionId: ID) => {
    // Try to find the input element, retrying a few times in case it needs to mount
    const input = newTaskInputRefs.current[sectionId];
    if (!input) {
      return;
    }

    requestAnimationFrame(() => focusOnEndOfEditableContent(input));
  };

  const handleAddTaskRef = useRef(handleAddTask);
  handleAddTaskRef.current = handleAddTask;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const sectionId = item.sourceId.replace(newTaskSuffix, '');
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.Section,
            groupSectionId: sectionId,
            groupSetting: groupSettings,
            today,
          });
          return (
            <NewTaskRow
              goal={prefill.goalId}
              lifeArea={prefill.lifeAreaId}
              section={prefill.sectionId}
              startTime={null}
              duration={null}
              tags={prefill.tagIds}
              newTaskInputRef={(element) =>
                (newTaskInputRefs.current[sectionId] = element)
              }
            />
          );
        }

        // Section
        if (type === TaskListItemTypes.Section) {
          const children = item.children ?? [];
          const taskCount = children.filter(
            (childId) => !childId.endsWith(newTaskSuffix),
          ).length;

          return (
            <SectionHeader
              item={itemInstance}
              denseFormat={denseFormat}
              onUpdateSectionName={onUpdateSectionRef.current}
              taskCount={taskCount}
              goalId={goalId}
              lifeAreaId={lifeAreaId}
              onAddTask={() => handleAddTaskRef.current(item.sourceId)}
            />
          );
        }

        return null;
      },
    [denseFormat, goalId, lifeAreaId, groupSettings, today],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        isItemDraggable={isItemDraggable}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
        dropTypes={dropTypes}
      />
    </Styled.Container>
  );
};
